import Papa from 'papaparse';

import {
  findHeaderMismatch,
  hasHeaderMismatch,
  normalizeHeader,
  readCsvHeaders,
} from 'utils/csvHeaders';

import { BulkAssetRow } from '../types';

interface ParseBulkAssetCsvOptions {
  includeRowsMissingVin?: boolean;
}

const COLUMN_HEADERS = {
  accountSourceId: 'accountid',
  depotFmsId: 'fmsid',
  vin: 'vin',
  unitNumber: 'unit',
  odometer: 'odometer',
  engineHours: 'enginehours',
};

// The columns of the current template, in the order the template prints them.
// Uploads must match this set exactly; see findHeaderMismatch.
export const TEMPLATE_COLUMNS = [
  'Account ID*',
  'FMS ID*',
  'VIN*',
  'Unit #*',
  'Odometer',
  'Engine Hours',
];

const cell = (
  row: Record<string, unknown>,
  normalizedHeader: string,
): string => {
  const key = Object.keys(row).find(
    (k) => normalizeHeader(k) === normalizedHeader,
  );
  return key ? String(row[key] ?? '').trim() : '';
};

// Coerce an optional integer cell (e.g. Odometer "150,987") to a number,
// stripping thousands separators. Empty/non-numeric cells become undefined.
const toInteger = (value: string): number | undefined => {
  const normalized = value.trim().replace(/[,\s]/g, '');
  if (!normalized || !/^\d+$/.test(normalized)) return undefined;
  return Number(normalized);
};

/** Resolves to the columns that do not line up with the current template. */
export const findTemplateMismatch = async (file: File) =>
  findHeaderMismatch(await readCsvHeaders(file), TEMPLATE_COLUMNS);

export const matchesCurrentTemplate = async (file: File): Promise<boolean> =>
  !hasHeaderMismatch(await findTemplateMismatch(file));

export const parseBulkAssetCsv = (
  file: File,
  options: ParseBulkAssetCsvOptions = {},
): Promise<BulkAssetRow[]> =>
  new Promise((resolve, reject) => {
    const { includeRowsMissingVin = false } = options;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = (result.data as Record<string, unknown>[]).reduce<
          BulkAssetRow[]
        >((acc, raw) => {
          const accountSourceId = cell(raw, COLUMN_HEADERS.accountSourceId);
          const depotFmsId = cell(raw, COLUMN_HEADERS.depotFmsId);
          const vin = cell(raw, COLUMN_HEADERS.vin);
          const unitNumber = cell(raw, COLUMN_HEADERS.unitNumber);
          const hasRequiredBaseFields =
            accountSourceId && depotFmsId && unitNumber;

          if (hasRequiredBaseFields && (vin || includeRowsMissingVin)) {
            acc.push({
              accountSourceId,
              depotFmsId,
              vin,
              unitNumber,
              mileage: toInteger(cell(raw, COLUMN_HEADERS.odometer)),
              engineHours: toInteger(cell(raw, COLUMN_HEADERS.engineHours)),
            });
          }

          return acc;
        }, []);

        resolve(rows);
      },
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });
